/**
 * A value (iff `ok`) or an error (iff `!ok`).
 */
export type Failable<TValue, TError> = ResolvedFailable<TValue> | FailedFailable<TError>;
export type ResolvedFailable<TValue> = {ok: true; value: TValue};
export type FailedFailable<TError> = {ok: false; error: TError};

/**
 * Creates a resolved `Failable`.
 * @param value The value to succeed with.
 * @returns
 */
export const resolve = <TValue>(value: TValue): ResolvedFailable<TValue> => ({ok: true, value});

/**
 * Creates a failed `Failable`.
 * @param error The error to fail with.
 * @returns
 */
export const fail = <TError>(error: TError): FailedFailable<TError> => ({ok: false, error});

/**
 * True iff a `Failable` is resolved. Also tightens the type of the `Failable`.
 * @param failable The `Failable` to test.
 * @returns True iff the `Failable` is resolved.
 */
export const isResolved = <TValue, TError>(failable: Failable<TValue, TError>): failable is ResolvedFailable<TValue> =>
    failable.ok;

/**
 * True iff a `Failable` failed. Also tightens the type of the `Failable`.
 * @param failable The `Failable` to test.
 * @returns True iff the `Failable` failed.
 */
export const isFailed = <TValue, TError>(failable: Failable<TValue, TError>): failable is FailedFailable<TError> =>
    !failable.ok;

/**
 * Returns the value of a failable.
 *
 * Throws an exception if the failable is not ok. Should really only be used
 * in **non-production** code and only when the programmer knows more about
 * the failable than the type checker does.
 *
 * @param failable The failable to coerce.
 * @returns The value in the failable.
 * @example
 *   expect(isResolved(actualResult)).toBe(true);
 *   expect(coerceToValue(actualResult)).toBe(expectedResult);
 */
export const coerceToValue = <TValue, TError>(failable: Failable<TValue, TError>): TValue => {
    if (!failable.ok) {
        throw new Error('Failable not ok.');
    }
    return failable.value;
};

/**
 * Traverses a list of `Failable` and returns a `Failable` list of values.
 *
 * Fails if there are at least one error in which case, collects all errors.
 *
 * @param values The list of `Failable`.
 * @returns A `Failable` with a list of the values.
 *
 * @example all([resolve(1), resolve(2)]) === resolve([1, 2])
 * @example all([resolve(1), fail('foo'), resolve(2), fail('bar')]) === fail(['foo', 'bar'])
 */
export const all = <TValue, TError>(values: Failable<TValue, TError>[]): Failable<TValue[], TError[]> => {
    return values.reduce(
        (acc: Failable<TValue[], TError[]>, current) => {
            if (!acc.ok) {
                if (!current.ok) {
                    return {ok: false, error: [...acc.error, current.error]};
                } else {
                    return acc;
                }
            }
            if (!current.ok) {
                return {ok: false, error: [current.error]};
            }
            return {ok: true, value: [...acc.value, current.value]};
        },
        {ok: true, value: []},
    );
};

/**
 * Applies a function to the value of a `Failable`.
 *
 * Keeps the error from the `Failable`.
 *
 * Functor map over `Failable<_, TError>`.
 */
export const map = <TValue, TValue2, TError>(
    failable: Failable<TValue, TError>,
    fun: (value: TValue) => TValue2,
): Failable<TValue2, TError> => (isResolved(failable) ? {ok: true, value: fun(failable.value)} : failable);

/**
 * Fails if there is at least one error, in which case it returns only the first error.
 *
 * @param values The list of `Failable`.
 * @returns A `Failable` with a list of the values or the first error encountered.
 */
export const pick = <TValue, TError>(value: Failable<TValue[], TError[]>): Failable<TValue[], TError> => {
    if (!isResolved(value)) {
        return fail(value.error[0]);
    }
    return value;
};

export const getError = <TValue, TError>(value: Failable<TValue, TError>): TError | null => {
    if (!isResolved(value)) {
        return value.error;
    }
    return null;
};
